import React, { cloneElement } from 'react';
import { ProgressBar } from '../../../components/ATS/Progress/ProgressBar';
import { Image } from '../../../components/image/Image';
import { Link } from '../../../components/Link';
import { ImageProps } from '../../../interfaces';
import { classes } from '../../../utils/commonUtils';
import css from './call-to-action-button.module.scss';

interface BaseCallToActionProps {
  text:
    | {
        primary: string;
        secondary?: string;
      }
    | string;
  image: ImageProps;
  icon?: JSX.Element | null;
  progress?: number;
  disabled?: boolean;
}

interface CallToActionButtonProps {
  onClick?: () => void;
}

interface CallToActionInternalLinkProps {
  to: string | URL;
}

interface CallToActionExternalLinkProps {
  href: string;
}

type CallToActionPropsUnion = CallToActionButtonProps | CallToActionInternalLinkProps | CallToActionExternalLinkProps;

export type CallToActionProps = BaseCallToActionProps & CallToActionPropsUnion;

type WithChildren<P extends object> = P & {
  children: React.ReactNode;
  className: string;
};

function CallToActionButton({ onClick, children, ...rest }: WithChildren<CallToActionButtonProps>) {
  return (
    <button {...rest} onClick={onClick}>
      {children}
    </button>
  );
}

function CallToActionInternalLink({ to, children, ...rest }: WithChildren<CallToActionInternalLinkProps>) {
  return (
    <Link {...rest} to={to}>
      {children}
    </Link>
  );
}

function CallToActionExternalLink({ href, children, ...rest }: WithChildren<CallToActionExternalLinkProps>) {
  return (
    <a {...rest} href={href} rel="noopener noreferrer" target="_blank">
      {children}
    </a>
  );
}

function CallToActionHandler(props: WithChildren<CallToActionPropsUnion>) {
  if ('to' in props) {
    const { to, children, ...rest } = props;
    return (
      <CallToActionInternalLink {...rest} to={to}>
        {children}
      </CallToActionInternalLink>
    );
  }

  if ('href' in props) {
    const { href, children, ...rest } = props;
    return (
      <CallToActionExternalLink {...rest} href={href}>
        {children}
      </CallToActionExternalLink>
    );
  }

  const { onClick, children, ...rest } = props;
  return (
    <CallToActionButton {...rest} onClick={onClick}>
      {children}
    </CallToActionButton>
  );
}

export function CallToAction(props: CallToActionProps) {
  const { text, image, icon, progress, disabled, ...rest } = props;

  // We don't use `disabled={disabled}` because it's not a valid HTML attribute
  // for every variant of the button. We also need the button to be focusable
  // even when it's disabled.
  return (
    <CallToActionHandler {...rest} className={css.button} aria-disabled={disabled}>
      <Image className={css.logo} src={image.src} alt={image.alt} />

      <CtaText text={text} className={css.text} />

      {icon !== null &&
        icon !== undefined &&
        cloneElement(icon, { className: classes(css.icon, icon.props.className) })}

      {progress !== undefined && <ProgressBar className={css.progress} value={progress} />}
    </CallToActionHandler>
  );
}

interface CtaTextProps {
  text: BaseCallToActionProps['text'];
  className?: string;
}

function CtaText({ text, className }: CtaTextProps) {
  if (typeof text === 'string') {
    return (
      <div className={className}>
        <span className={css.title}>{text}</span>
      </div>
    );
  }

  return (
    <div className={className}>
      <span className={css.title}>{text.primary}</span>
      {text.secondary !== undefined && <span className={css.subtitle}>{text.secondary}</span>}
    </div>
  );
}

export function CallToActionSkeleton() {
  return (
    <div className={classes(css.button, css.skeleton)}>
      <div className={css.logo} />

      <div className={css.text}>
        <span className={css.title} />
        <span className={css.subtitle} />
      </div>

      <div className={css.icon} />
    </div>
  );
}

CallToAction.Skeleton = CallToActionSkeleton;
